import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import CookieBanner from '../../components/feature/CookieBanner';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import OptimizedImage from '../../components/base/OptimizedImage';
import Breadcrumbs from '../../components/feature/Breadcrumbs';
import { Link } from 'react-router-dom';

const FaqPage = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    document.title = 'Preguntas Frecuentes - PLADUREX | Dudas sobre Pladur en Sabadell';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Resuelve tus dudas sobre instalación de pladur en Sabadell: plazos, precios, aislamiento acústico, pladur antihumedad, acabados. Respuestas de profesionales ☎ 629 501 941');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'preguntas pladur, dudas instalación pladur Sabadell, FAQ pladur, precios pladur');
    }

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "¿Cuánto tarda la instalación de pladur en Sabadell?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "El tiempo depende del tipo y tamaño del proyecto. Un tabique estándar de una habitación se puede instalar en 1-2 días. Un falso techo completo de un piso puede llevar entre 2 y 4 días. Los trasdosados de varias paredes pueden requerir 3-5 días. Siempre te daremos un plazo estimado en el presupuesto."
          }
        },
        {
          "@type": "Question",
          "name": "¿Qué factores influyen en el precio de una instalación de pladur?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Los principales factores son: metros cuadrados a instalar, altura de techos, tipo de placa (estándar, hidrófuga, ignífuga), si incluye aislamiento acústico o térmico, número de puertas o pasos, complejidad del diseño, nivel de acabado requerido y accesibilidad de la zona de trabajo."
          }
        },
        {
          "@type": "Question",
          "name": "¿El pladur aísla del ruido entre habitaciones?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, el pladur con aislamiento acústico interior reduce significativamente el ruido. Utilizamos lana mineral de alta densidad en el interior del tabique o trasdosado, y en casos que requieren mayor aislamiento, aplicamos sistemas de doble placa. El nivel de aislamiento depende del grosor y tipo de material utilizado."
          }
        },
        {
          "@type": "Question",
          "name": "¿El pladur también aísla térmicamente?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, los trasdosados con aislamiento térmico mejoran notablemente el confort y reducen el consumo energético. Instalamos lana mineral o poliestireno en el interior, lo que ayuda a mantener la temperatura en invierno y verano. Es especialmente efectivo en fachadas exteriores y paredes frías."
          }
        },
        {
          "@type": "Question",
          "name": "¿Qué es el pladur antihumedad y dónde se usa?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "El pladur antihumedad son placas hidrófugas (de color verde) especialmente tratadas para resistir ambientes húmedos. Se utilizan en baños, cocinas, lavaderos, sótanos y cualquier zona con alta humedad. Previenen la aparición de moho y garantizan mayor durabilidad en estas áreas."
          }
        },
        {
          "@type": "Question",
          "name": "¿Los acabados de pladur quedan listos para pintar?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, realizamos el tratamiento completo de juntas, aplicamos pasta, lijamos y dejamos la superficie completamente lisa y lista para pintar. Solo necesitarás aplicar la pintura que elijas. Si prefieres alicatar (en baños o cocinas), la superficie también queda preparada para ello."
          }
        },
        {
          "@type": "Question",
          "name": "¿Se puede colgar peso en las paredes de pladur?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, se pueden colgar cuadros, estanterías, muebles y televisores usando tacos especiales para pladur. Para cargas más pesadas (muebles de cocina, radiadores), instalamos refuerzos de madera o perfiles metálicos en el interior durante la instalación. Es importante indicarlo en la fase de planificación."
          }
        },
        {
          "@type": "Question",
          "name": "¿Cuánto ruido y suciedad genera la instalación?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "El pladur es obra seca, por lo que genera mucha menos suciedad que la obra tradicional. Hay algo de polvo al cortar placas y lijar juntas, pero protegemos suelos y muebles, y recogemos los escombros diariamente. El ruido es moderado (corte de perfiles y atornillado) y podemos adaptar horarios si la vivienda está habitada."
          }
        },
        {
          "@type": "Question",
          "name": "¿Hacéis presupuestos sin compromiso?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sí, todos nuestros presupuestos son gratuitos y sin compromiso. Visitamos tu proyecto en Sabadell o alrededores, tomamos medidas, evaluamos las necesidades y te entregamos un presupuesto detallado con materiales, mano de obra y plazos. No hay ninguna obligación de contratación."
          }
        },
        {
          "@type": "Question",
          "name": "¿Necesito permisos para instalar pladur en mi vivienda?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Para obras interiores de pladur en viviendas particulares (tabiques, techos, trasdosados) normalmente no se requieren permisos especiales, ya que no son obras estructurales. Sin embargo, si vives en una comunidad de propietarios, es recomendable comunicarlo. Para locales comerciales o cambios de uso, puede ser necesario consultar con el ayuntamiento."
          }
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const faqs = [
    {
      question: '¿Cuánto tarda la instalación de pladur en Sabadell?',
      answer: 'El tiempo depende del tipo y tamaño del proyecto. Un tabique estándar de una habitación se puede instalar en 1-2 días. Un falso techo completo de un piso puede llevar entre 2 y 4 días. Los trasdosados de varias paredes pueden requerir 3-5 días. Siempre te daremos un plazo estimado en el presupuesto.'
    },
    {
      question: '¿Qué factores influyen en el precio de una instalación de pladur?',
      answer: 'Los principales factores son: metros cuadrados a instalar, altura de techos, tipo de placa (estándar, hidrófuga, ignífuga), si incluye aislamiento acústico o térmico, número de puertas o pasos, complejidad del diseño, nivel de acabado requerido y accesibilidad de la zona de trabajo.'
    },
    {
      question: '¿El pladur aísla del ruido entre habitaciones?',
      answer: 'Sí, el pladur con aislamiento acústico interior reduce significativamente el ruido. Utilizamos lana mineral de alta densidad en el interior del tabique o trasdosado, y en casos que requieren mayor aislamiento, aplicamos sistemas de doble placa. El nivel de aislamiento depende del grosor y tipo de material utilizado.'
    },
    {
      question: '¿El pladur también aísla térmicamente?',
      answer: 'Sí, los trasdosados con aislamiento térmico mejoran notablemente el confort y reducen el consumo energético. Instalamos lana mineral o poliestireno en el interior, lo que ayuda a mantener la temperatura en invierno y verano. Es especialmente efectivo en fachadas exteriores y paredes frías.'
    },
    {
      question: '¿Qué es el pladur antihumedad y dónde se usa?',
      answer: 'El pladur antihumedad son placas hidrófugas (de color verde) especialmente tratadas para resistir ambientes húmedos. Se utilizan en baños, cocinas, lavaderos, sótanos y cualquier zona con alta humedad. Previenen la aparición de moho y garantizan mayor durabilidad en estas áreas.'
    },
    {
      question: '¿Los acabados de pladur quedan listos para pintar?',
      answer: 'Sí, realizamos el tratamiento completo de juntas, aplicamos pasta, lijamos y dejamos la superficie completamente lisa y lista para pintar. Solo necesitarás aplicar la pintura que elijas. Si prefieres alicatar (en baños o cocinas), la superficie también queda preparada para ello.'
    },
    {
      question: '¿Se puede colgar peso en las paredes de pladur?',
      answer: 'Sí, se pueden colgar cuadros, estanterías, muebles y televisores usando tacos especiales para pladur. Para cargas más pesadas (muebles de cocina, radiadores), instalamos refuerzos de madera o perfiles metálicos en el interior durante la instalación. Es importante indicarlo en la fase de planificación.'
    },
    {
      question: '¿Cuánto ruido y suciedad genera la instalación?',
      answer: 'El pladur es obra seca, por lo que genera mucha menos suciedad que la obra tradicional. Hay algo de polvo al cortar placas y lijar juntas, pero protegemos suelos y muebles, y recogemos los escombros diariamente. El ruido es moderado (corte de perfiles y atornillado) y podemos adaptar horarios si la vivienda está habitada.'
    },
    {
      question: '¿Hacéis presupuestos sin compromiso?',
      answer: 'Sí, todos nuestros presupuestos son gratuitos y sin compromiso. Visitamos tu proyecto en Sabadell o alrededores, tomamos medidas, evaluamos las necesidades y te entregamos un presupuesto detallado con materiales, mano de obra y plazos. No hay ninguna obligación de contratación.'
    },
    {
      question: '¿Necesito permisos para instalar pladur en mi vivienda?',
      answer: 'Para obras interiores de pladur en viviendas particulares (tabiques, techos, trasdosados) normalmente no se requieren permisos especiales, ya que no son obras estructurales. Sin embargo, si vives en una comunidad de propietarios, es recomendable comunicarlo. Para locales comerciales o cambios de uso, puede ser necesario consultar con el ayuntamiento.'
    }
  ];

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-dark-bg text-light-text">
      <Header />
      <Breadcrumbs />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="https://public.readdy.ai/ai/img_res/bf9732eba72f2ac655f3250a91d1576b.jpg"
            alt="Preguntas frecuentes"
            loading="lazy"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-dark-bg"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
              Preguntas <span className="text-primary-orange">frecuentes</span>
            </h1>
            <p className="text-xl md:text-2xl text-light-muted max-w-3xl mx-auto">
              Resolvemos todas tus dudas sobre la instalación de pladur en Sabadell
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="sr-only">Lista de preguntas frecuentes sobre pladur</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-dark-card rounded-xl border border-dark-lighter overflow-hidden"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-dark-lighter/30 transition-colors cursor-pointer"
                    aria-expanded={openIndex === index}
                  >
                    <h3 className="text-lg font-semibold pr-4">{faq.question}</h3>
                    <motion.div
                      animate={{ rotate: openIndex === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className="flex-shrink-0"
                    >
                      <i className="ri-arrow-down-s-line text-2xl text-primary-orange"></i>
                    </motion.div>
                  </button>
                  
                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-5 pt-2">
                          <p className="text-light-muted leading-relaxed">{faq.answer}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-dark-card">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 flex items-center justify-center bg-primary-orange/10 rounded-full mx-auto mb-6">
                <i className="ri-question-answer-line text-3xl text-primary-orange"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
                ¿No encuentras tu respuesta?
              </h2>
              <p className="text-lg text-light-muted mb-8">
                <Link to="/contacto" className="text-primary-orange hover:underline">Contacta con nosotros</Link> y resolveremos todas tus dudas sobre tu proyecto de pladur en Sabadell
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href="tel:+34629501941"
                  className="flex items-center space-x-2 px-8 py-4 bg-primary-orange text-white rounded-lg font-semibold text-lg hover:shadow-glow-orange transition-all whitespace-nowrap"
                >
                  <i className="ri-phone-line text-xl"></i>
                  <span>629 501 941</span>
                </a>
                <a
                  href="https://wa.me/34629501941"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 px-8 py-4 border-2 border-primary-orange text-primary-orange rounded-lg font-semibold text-lg hover:bg-primary-orange hover:text-white transition-all whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  <span>WhatsApp</span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
                Más <span className="text-primary-orange">información</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.a
                href="/servicios"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className="bg-dark-card p-6 rounded-xl border border-dark-lighter hover:border-primary-orange/50 transition-all cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary-orange/10 rounded-lg mb-4">
                  <i className="ri-service-line text-2xl text-primary-orange"></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">Nuestros Servicios</h3>
                <p className="text-light-muted text-sm">Conoce todos los servicios de pladur que ofrecemos</p>
              </motion.a>

              <motion.a
                href="/empresa"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-dark-card p-6 rounded-xl border border-dark-lighter hover:border-primary-orange/50 transition-all cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary-orange/10 rounded-lg mb-4">
                  <i className="ri-building-line text-2xl text-primary-orange"></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">La Empresa</h3>
                <p className="text-light-muted text-sm">Descubre cómo trabajamos y nuestros valores</p>
              </motion.a>

              <motion.a
                href="/contacto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                whileHover={{ y: -5 }}
                className="bg-dark-card p-6 rounded-xl border border-dark-lighter hover:border-primary-orange/50 transition-all cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary-orange/10 rounded-lg mb-4">
                  <i className="ri-mail-line text-2xl text-primary-orange"></i>
                </div>
                <h3 className="text-lg font-semibold mb-2">Contacto</h3>
                <p className="text-light-muted text-sm">Solicita tu presupuesto sin compromiso</p>
              </motion.a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <CookieBanner />
      <ScrollToTopButton />
    </div>
  );
};

export default FaqPage;
